/**
 * Sophie Guidolin - Theme JavaScript
 * Header scroll, mobile menu, scroll reveal, FAQ accordion,
 * YouTube lazy load, Klaviyo newsletter
 */

(function() {
  'use strict';

  /* --- Header Scroll Behaviour --- */
  var header = document.getElementById('SiteHeader');
  if (header) {
    /* On non-homepage pages the header starts with .is-scrolled (solid dark bg).
       We must never remove it on those pages, otherwise white text becomes
       invisible against the light page background. */
    var isHomepage = document.body.classList.contains('template-index');

    if (isHomepage) {
      /* Homepage: transparent at top, solid dark after 60px scroll */
      window.addEventListener('scroll', function() {
        header.classList.toggle('is-scrolled', window.scrollY > 60);
      }, { passive: true });
    }
    /* Inner pages: is-scrolled is set server-side and never removed */
  }

  /* --- Mobile Menu Toggle --- */
  var menuToggle = document.getElementById('MobileMenuToggle');
  var mobileMenu = document.getElementById('MobileMenu');

  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener('click', function() {
      var isOpen = mobileMenu.classList.toggle('is-open');
      menuToggle.classList.toggle('is-active');
      menuToggle.setAttribute('aria-expanded', isOpen);
      mobileMenu.setAttribute('aria-hidden', !isOpen);
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });

    mobileMenu.querySelectorAll('a').forEach(function(link) {
      link.addEventListener('click', function() {
        mobileMenu.classList.remove('is-open');
        menuToggle.classList.remove('is-active');
        menuToggle.setAttribute('aria-expanded', 'false');
        mobileMenu.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
    });
  }

  /* --- Scroll Reveal with Staggered Child Animations --- */
  if ('IntersectionObserver' in window) {
    /* Respect prefers-reduced-motion */
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Child elements to stagger in sequence within each section */
    var STAGGER_SELECTORS = [
      '.eyebrow',
      'h1, h2, h3',
      '.about__body p, .philosophy__body p, .the-system__body, p.body-large, p.body-small',
      '.btn, .about__cta-wrap, .the-system__cta, .my-story-cta__buttons',
      '.credential-card, .testimonial-card, .youtube-card, .elite__inclusion',
      '.the-system__stat, .faq__item'
    ];

    function prepareChildren(section) {
      if (prefersReduced) return;
      var delay = 0;
      var seen = new WeakSet();
      STAGGER_SELECTORS.forEach(function(sel) {
        section.querySelectorAll(sel).forEach(function(child) {
          if (seen.has(child)) return;
          /* Only animate direct descendants of this section, not nested section-reveals */
          var parentSection = child.closest('.section-reveal');
          if (parentSection !== section) return;
          seen.add(child);
          child.style.opacity = '0';
          child.style.transform = 'translateY(14px)';
          child.style.transition = 'opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1) ' + delay + 'ms, transform 0.65s cubic-bezier(0.22, 1, 0.36, 1) ' + delay + 'ms';
          child.dataset.revealDelay = delay;
          delay = Math.min(delay + 90, 450);
        });
      });
    }

    function revealChildren(section) {
      if (prefersReduced) return;
      section.querySelectorAll('[data-reveal-delay]').forEach(function(child) {
        child.style.opacity = '1';
        child.style.transform = 'translateY(0)';
      });
    }

    var revealObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealChildren(entry.target);
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.section-reveal').forEach(function(el) {
      prepareChildren(el);
      revealObserver.observe(el);
    });
  }

  /* --- Portrait Reveal Animation --- */
  if ('IntersectionObserver' in window) {
    var portraitObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        portraitObserver.unobserve(entry.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    document.querySelectorAll('[data-portrait-reveal]').forEach(function(el) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        el.classList.add('is-visible');
        return;
      }
      portraitObserver.observe(el);
    });
  }

  /* --- Count-Up Animation for Stat Numbers --- */
  if ('IntersectionObserver' in window) {
    var countUpObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        countUpObserver.unobserve(el);
        var raw = el.getAttribute('data-count-target');
        var suffix = el.getAttribute('data-count-suffix') || '';
        var prefix = el.getAttribute('data-count-prefix') || '';
        var target = parseFloat(raw);
        var isDecimal = raw.indexOf('.') !== -1;
        var duration = 1800;
        var startTime = null;
        /* Respect prefers-reduced-motion */
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          el.textContent = prefix + (isDecimal ? target.toFixed(1) : Math.round(target).toLocaleString()) + suffix;
          return;
        }
        function easeOutExpo(t) {
          return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        }
        function step(timestamp) {
          if (!startTime) startTime = timestamp;
          var progress = Math.min((timestamp - startTime) / duration, 1);
          var eased = easeOutExpo(progress);
          var current = target * eased;
          el.textContent = prefix + (isDecimal ? current.toFixed(1) : Math.round(current).toLocaleString()) + suffix;
          if (progress < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.3 });

    document.querySelectorAll('.the-system__stat-number, .credential-card__stat-number').forEach(function(el) {
      var text = el.textContent.trim();
      /* Parse suffix (+ or %) and prefix ($) */
      var suffix = '';
      var prefix = '';
      var numStr = text;
      if (numStr.charAt(0) === '$') { prefix = '$'; numStr = numStr.slice(1); }
      if (numStr.slice(-1) === '+') { suffix = '+'; numStr = numStr.slice(0, -1); }
      if (numStr.slice(-1) === '%') { suffix = '%'; numStr = numStr.slice(0, -1); }
      /* Remove commas for parsing */
      var num = parseFloat(numStr.replace(/,/g, ''));
      if (isNaN(num)) return; /* Skip non-numeric like "1M+" — leave as-is */
      el.setAttribute('data-count-target', numStr.replace(/,/g, ''));
      el.setAttribute('data-count-suffix', suffix);
      el.setAttribute('data-count-prefix', prefix);
      el.textContent = prefix + '0' + suffix;
      countUpObserver.observe(el);
    });
  }

  /* --- Smooth Scroll for Anchor Links --- */
  document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
    anchor.addEventListener('click', function(e) {
      var targetId = this.getAttribute('href');
      if (targetId === '#') return;
      var target = document.querySelector(targetId);
      if (target) {
        e.preventDefault();
        var headerHeight = header ? header.offsetHeight : 0;
        var targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      }
    });
  });

  /* --- FAQ Accordion --- */
  document.querySelectorAll('.faq__question').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var item = this.closest('.faq__item');
      var isOpen = item.classList.contains('is-open');
      // Close all
      document.querySelectorAll('.faq__item').forEach(function(i) {
        i.classList.remove('is-open');
        i.querySelector('.faq__question').setAttribute('aria-expanded', 'false');
      });
      // Open clicked if it was closed
      if (!isOpen) {
        item.classList.add('is-open');
        this.setAttribute('aria-expanded', 'true');
      }
    });
  });

  /* --- YouTube Lazy Load --- */
  window.playVideo = function(card, videoId) {
    var thumb = card.querySelector('.youtube-card__thumb');
    if (!thumb || !videoId) return;
    thumb.innerHTML = '<iframe class="youtube-card__iframe" src="https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
  };

})();
